/**
 * DA-style before/after grid — the non-time-series proposal evidence
 * (design session 2026-08-22; replaces InputDeltaSummary per Brendan's
 * ruling: "the table should replace the summary").
 *
 * Fork lineage: AgenticV2/cards/results/driver-analysis/GenerateChartPanel
 * (itself a v2 fork of the Reports DA panel) — same ag-Grid shape (fixed
 *-height internal scroll viewport, 40px sortable rows), reshaped from a
 * driver-contribution table into before/after evidence:
 *
 *   Segment | Current | Proposed | paired bars
 *
 * Values are FormatKey-formatted via the lifted Visualization/
 * getFormattedValue (the EA grid's own formatter — parity by
 * construction). `columns_meta.format_key === null` (unresolved input,
 * or a proposal stored before the backend landed columns_meta) renders
 * the raw value — a half-formatted table being worse than an unformatted
 * one is the standing principle, but an honest raw number beats hiding
 * the table entirely for old runs.
 *
 * U&E treat-as-SVA (Brendan's design): the grid shows ONE parameter
 * column at a time — the changed one. Changed columns are derived
 * client-side (any row where before[i] !== after[i]). When more than one
 * column changed (possible for future multi-parameter patches, or
 * coupled inputs), a toggle above the grid flips between them; with one
 * changed column — today's reality — the toggle doesn't render at all.
 *
 * Bars render only when the active column's `columns_meta.bar` is true
 * (deterministic server-side rule — date/positional/flag parameters are
 * bar-less) AND at least one positive numeric value exists. The paired
 * bars share one maxAbs across all rows and both series, so length is
 * comparable everywhere (see PairedBarCell for the encoding rules).
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';

import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { getFormattedValue } from 'src/components/Visualization/getFormattedValue';

import { InputDeltaColumnMeta, InputDeltaInput } from '../types';

import PairedBarCell, {
  CURRENT_BAR_COLOR,
  PROPOSED_BAR_COLOR,
} from './PairedBarCell';

interface InputDeltaGridProps {
  input: InputDeltaInput;
  // Multi-input proposals label each grid with its input name; a single
  // input's name is already in the headline (2026-08-22 mark 2 ruling,
  // carried over from the summary).
  showInputName?: boolean;
}

interface DeltaRowDatum {
  label: string;
  before: number | string | null;
  after: number | string | null;
}

// Strips IEEE-754 noise (5.000000000000004 -> "5") for unformatted
// numerics — same idiom the summary used; never scientific notation at
// this surface's magnitudes.
const plainNumber = (value: number): string =>
  parseFloat(value.toFixed(4)).toString();

const formatCell = (
  value: number | string | null | undefined,
  formatKey: number | null
): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (formatKey !== null) {
    // getFormattedValue returns null for falsy numerics in its '$' and
    // default branches (EA-grid quirk, preserved verbatim in the lift) —
    // on an approval gate a genuine 0 must render as "0", not blank.
    const formatted = getFormattedValue(value, formatKey);
    if (formatted !== null && formatted !== '') return String(formatted);
  }
  return plainNumber(value);
};

// Numeric-aware sort for mixed cells: numbers order numerically, text
// and blanks sink below numbers and order lexically among themselves.
const cellComparator = (a: unknown, b: unknown): number => {
  const na = typeof a === 'number' ? a : Number.NEGATIVE_INFINITY;
  const nb = typeof b === 'number' ? b : Number.NEGATIVE_INFINITY;
  if (na !== nb) return na < nb ? -1 : 1;
  return String(a ?? '').localeCompare(String(b ?? ''));
};

const GRID_ROW_HEIGHT = 40;
const GRID_HEADER_HEIGHT = 40;
const GRID_MAX_HEIGHT = 360;

const LegendChip: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '2px',
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const InputDeltaGrid: React.FC<InputDeltaGridProps> = ({
  input,
  showInputName = false,
}) => {
  const rows = useMemo(() => input.rows ?? [], [input]);

  // columns_meta fallback for pre-18771f9 proposals: derive from the row
  // columns with no format key (raw display) and bars on.
  const columnsMeta: InputDeltaColumnMeta[] = useMemo(() => {
    if (input.columns_meta?.length) return input.columns_meta;
    const names = rows[0]?.columns ?? input.x ?? [];
    return names.map((name) => ({ name, format_key: null, bar: true }));
  }, [input, rows]);

  // Changed columns: any row whose before/after differ at that index.
  // Rows are changed-only by construction, so this is never empty in a
  // well-formed payload — the all-columns fallback covers a degraded one.
  const changedColumnIdxs = useMemo(() => {
    const changed = columnsMeta
      .map((_, idx) => idx)
      .filter((idx) =>
        rows.some(
          (row) => (row.before?.[idx] ?? null) !== (row.after?.[idx] ?? null)
        )
      );
    return changed.length > 0 ? changed : columnsMeta.map((_, idx) => idx);
  }, [columnsMeta, rows]);

  // Self-healing selection (no effect needed): an explicit pick that no
  // longer exists — a different input re-rendered through this component
  // — falls back to the first changed column.
  const [pickedIdx, setPickedIdx] = useState<number | null>(null);
  const activeIdx =
    pickedIdx !== null && changedColumnIdxs.includes(pickedIdx)
      ? pickedIdx
      : changedColumnIdxs[0] ?? 0;
  const activeMeta = columnsMeta[activeIdx] ?? {
    name: '',
    format_key: null,
    bar: true,
  };

  const rowData: DeltaRowDatum[] = useMemo(
    () =>
      rows.map((row) => ({
        label: row.label,
        before: row.before?.[activeIdx] ?? null,
        after: row.after?.[activeIdx] ?? null,
      })),
    [rows, activeIdx]
  );

  // Shared scale across all rows AND both series — bar lengths must be
  // comparable everywhere or they lie.
  const maxAbs = useMemo(
    () =>
      Math.max(
        0,
        ...rowData.flatMap((row) =>
          [row.before, row.after].map((v) =>
            typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0
          )
        )
      ),
    [rowData]
  );
  const showBars = activeMeta.bar && maxAbs > 0;

  const columnDefs: ColDef<DeltaRowDatum>[] = useMemo(() => {
    const textCellStyle = {
      display: 'flex',
      alignItems: 'center',
      fontSize: 13,
    };
    const numberCellStyle = {
      ...textCellStyle,
      justifyContent: 'flex-end',
    };
    const fmt = (p: { value?: DeltaRowDatum['before'] }) =>
      formatCell(p.value, activeMeta.format_key);

    const defs: ColDef<DeltaRowDatum>[] = [
      {
        field: 'label',
        headerName: 'Segment',
        headerTooltip: 'Segment',
        sortable: true,
        sort: 'asc',
        cellStyle: textCellStyle,
        flex: 30,
        minWidth: 150,
      },
      {
        field: 'before',
        headerName: 'Current',
        headerTooltip: 'Current',
        headerClass: 'ag-right-aligned-header',
        sortable: true,
        comparator: cellComparator,
        valueFormatter: fmt,
        cellStyle: numberCellStyle,
        flex: 15,
        minWidth: 110,
      },
      {
        field: 'after',
        headerName: 'Proposed',
        headerTooltip: 'Proposed',
        headerClass: 'ag-right-aligned-header',
        sortable: true,
        comparator: cellComparator,
        valueFormatter: fmt,
        // The proposed value is the evidence — bold it where it actually
        // differs from current (multi-column changes can leave a row
        // unchanged on the active column).
        cellStyle: (p) => ({
          ...numberCellStyle,
          fontWeight: p.data && p.data.before !== p.data.after ? 600 : 400,
        }),
        flex: 15,
        minWidth: 110,
      },
    ];

    if (showBars) {
      defs.push({
        colId: 'bars',
        headerName: '',
        sortable: true,
        // Sorting the bar column orders by the proposed magnitude —
        // blanks and text sink to the bottom.
        valueGetter: (p) =>
          typeof p.data?.after === 'number' ? p.data.after : null,
        comparator: cellComparator,
        cellRenderer: PairedBarCell,
        cellRendererParams: { maxAbs },
        cellStyle: { display: 'flex', alignItems: 'center' },
        flex: 30,
        minWidth: 220,
      });
    }
    return defs;
  }, [activeMeta.format_key, showBars, maxAbs]);

  if (rows.length === 0) return null;

  const gridHeight =
    Math.min(
      GRID_HEADER_HEIGHT + rows.length * GRID_ROW_HEIGHT,
      GRID_MAX_HEIGHT
    ) + 2;

  // mb only, no top margin (2026-08-23, screenshot -20): my:3's top half
  // stacked onto the controls row's mb into ~40px of dead band above the
  // legend. Stacked multi-input blocks stay separated by the previous
  // block's bottom margin. InputDeltaChart matches.
  return (
    <Box sx={{ mb: 3 }}>
      {showInputName && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {input.input_name}
        </Typography>
      )}

      {changedColumnIdxs.length > 1 && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeIdx}
          onChange={(_e, next: number | null) => {
            if (next !== null) setPickedIdx(next);
          }}
          sx={{ mb: 1 }}
        >
          {changedColumnIdxs.map((idx) => (
            <ToggleButton key={columnsMeta[idx].name} value={idx}>
              {columnsMeta[idx].name}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      {showBars && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mb: 0.5,
          }}
        >
          <LegendChip color={CURRENT_BAR_COLOR} label="Current" />
          <LegendChip color={PROPOSED_BAR_COLOR} label="Proposed" />
        </Box>
      )}

      <Box
        className="ag-theme-alpine"
        sx={{ height: gridHeight, width: '100%' }}
      >
        <AgGridReact<DeltaRowDatum>
          animateRows
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{ suppressMenu: true, resizable: false }}
          domLayout="normal"
          rowHeight={GRID_ROW_HEIGHT}
          headerHeight={GRID_HEADER_HEIGHT}
          pagination={false}
          suppressRowClickSelection
          suppressPaginationPanel
          suppressMovableColumns
          suppressContextMenu
          enableBrowserTooltips
          onGridReady={(e) => e.api.sizeColumnsToFit()}
        />
      </Box>
    </Box>
  );
};

export default InputDeltaGrid;
